/** Ref-count in case multiple instances ever stack (e.g. editor previews). */
let customSampleDownloadScrollLockCount = 0;

/**
 * The Shopify customer form reloads the page, so the email is carried across
 * that reload in sessionStorage. The app proxy needs it to find the lead.
 */
const CUSTOM_SAMPLE_DOWNLOAD_EMAIL_KEY = 'custom-sample-download:email';
const CUSTOM_PDF_DOWNLOAD_CONSENT_KEY = 'custom-pdf-download:consent';

/** Keep consent tied to the submitted PDF form and signed-in account. */
function rememberCustomPdfDownloadConsent(formId, customerId) {
  try {
    window.sessionStorage.setItem(CUSTOM_PDF_DOWNLOAD_CONSENT_KEY, JSON.stringify({ formId, customerId }));
  } catch (error) {
    /* Without storage, only an already-tagged account can download on reload. */
  }
}

function readCustomPdfDownloadConsent(formId, customerId) {
  try {
    const consent = JSON.parse(window.sessionStorage.getItem(CUSTOM_PDF_DOWNLOAD_CONSENT_KEY) || 'null');
    return consent?.formId === formId && consent?.customerId === customerId;
  } catch (error) {
    return false;
  }
}

function clearCustomPdfDownloadConsent() {
  try {
    window.sessionStorage.removeItem(CUSTOM_PDF_DOWNLOAD_CONSENT_KEY);
  } catch (error) {
    /* storage unavailable */
  }
}

/** @param {string} email */
function rememberCustomSampleDownloadEmail(email) {
  try {
    window.sessionStorage.setItem(CUSTOM_SAMPLE_DOWNLOAD_EMAIL_KEY, email);
  } catch (error) {
    /* storage unavailable (private mode); logged-in customers still work */
  }
}

/** @returns {string} */
function readCustomSampleDownloadEmail() {
  try {
    return window.sessionStorage.getItem(CUSTOM_SAMPLE_DOWNLOAD_EMAIL_KEY) || '';
  } catch (error) {
    return '';
  }
}

function acquireCustomSampleDownloadScrollLock() {
  customSampleDownloadScrollLockCount += 1;
  if (customSampleDownloadScrollLockCount === 1) {
    const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    document.documentElement.style.setProperty(
      '--custom-sample-download-scrollbar-gutter',
      scrollbarWidth ? `${scrollbarWidth}px` : '0px'
    );
    document.body.classList.add('custom-sample-download-open');
  }
}

function releaseCustomSampleDownloadScrollLock() {
  if (customSampleDownloadScrollLockCount === 0) return;
  customSampleDownloadScrollLockCount -= 1;
  if (customSampleDownloadScrollLockCount > 0) return;

  document.documentElement.style.removeProperty('--custom-sample-download-scrollbar-gutter');
  document.body.classList.remove('custom-sample-download-open');
}

/**
 * Sample-download lead magnet: native dialog + scroll lock.
 *
 * @extends {HTMLElement}
 *
 * @property {boolean} [initialized]
 * @property {string} productId
 * @property {string} productHandle
 * @property {string} downloadEndpoint
 * @property {string} requestField
 * @property {string} requestValue
 * @property {string} formId
 * @property {string} customerAccountId
 * @property {boolean} downloadConsent
 * @property {boolean} [downloadInFlight]
 * @property {HTMLButtonElement | null} triggerElement
 * @property {HTMLDialogElement | null} dialogElement
 * @property {HTMLElement | null} formStateElement
 * @property {HTMLElement | null} successStateElement
 * @property {HTMLElement | null} formTitleElement
 * @property {HTMLElement | null} successTitleElement
 * @property {HTMLFormElement | null} formElement
 * @property {HTMLElement | null} errorElement
 * @property {HTMLButtonElement | null} fallbackButton
 * @property {HTMLElement | null} fallbackDefaultLabel
 * @property {HTMLElement | null} fallbackBusyLabel
 * @property {HTMLElement | null} downloadErrorElement
 * @property {HTMLButtonElement | null} submitButton
 * @property {HTMLElement | null} submitDefaultLabel
 * @property {HTMLElement | null} submitBusyLabel
 */
class CustomSampleDownload extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.productId = this.dataset.productId || '';
    this.productHandle = this.dataset.productHandle || '';
    this.downloadEndpoint = this.dataset.downloadEndpoint || '';
    // Samples send `productId`; other downloads (blog PDFs) name their own field.
    this.requestField = this.dataset.requestField || 'productId';
    this.requestValue = this.dataset.requestValue || this.productId;
    this.formId = this.dataset.formId || '';
    this.customerAccountId = this.dataset.customerAccountId || '';
    this.downloadConsent = false;

    this.triggerElement = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('[data-sample-download-trigger]')
    );
    this.dialogElement = /** @type {HTMLDialogElement | null} */ (
      this.querySelector('[data-sample-download-dialog]')
    );
    this.formStateElement = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-sample-download-form-state]')
    );
    this.successStateElement = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-sample-download-success-state]')
    );
    this.formTitleElement = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-sample-download-title-form]')
    );
    this.successTitleElement = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-sample-download-title-success]')
    );
    this.formElement = /** @type {HTMLFormElement | null} */ (
      this.querySelector('form.custom-sample-download__form')
    );
    this.errorElement = /** @type {HTMLElement | null} */ (this.querySelector('[data-sample-download-error]'));
    this.fallbackButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('[data-sample-download-fallback]')
    );
    this.fallbackDefaultLabel = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-sample-download-fallback-default]')
    );
    this.fallbackBusyLabel = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-sample-download-fallback-busy]')
    );
    this.downloadErrorElement = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-sample-download-download-error]')
    );
    this.submitButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('[data-sample-download-submit]')
    );
    this.submitDefaultLabel = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-sample-download-submit-default]')
    );
    this.submitBusyLabel = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-sample-download-submit-busy]')
    );

    if (!this.triggerElement || !this.dialogElement) return;

    this.bindEvents();

    const url = new URL(window.location.href);
    const hash = url.hash.replace(/^#/, '');
    const isCustomerPosted = url.searchParams.get('customer_posted') === 'true';
    const hashMatchesThisForm = !!this.formId && hash === this.formId;

    if (isCustomerPosted && hashMatchesThisForm) {
      this.showSuccessState();
      this.openDialog();
      this.dispatchCustomEvent('custom:sample-download:completed', {
        productId: this.productId,
        productHandle: this.productHandle,
        via: 'page-reload',
      });
      if (this.customerAccountId && readCustomPdfDownloadConsent(this.formId, this.customerAccountId)) {
        this.downloadConsent = true;
        void this.handleDownloadClick();
      }
    } else if (this.errorElement && hashMatchesThisForm) {
      this.openDialog();
    }
  }

  bindEvents() {
    const trigger = this.triggerElement;
    const dialog = this.dialogElement;
    if (!trigger || !dialog) return;

    trigger.addEventListener('click', () => this.openDialog());

    const closeButtons = this.querySelectorAll('[data-sample-download-close]');
    closeButtons.forEach((closeButton) => {
      closeButton.addEventListener('click', () => this.closeDialog());
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) {
        this.closeDialog();
      }
    });

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.closeDialog();
    });

    if (this.formElement) {
      this.formElement.addEventListener('submit', (event) => this.handleSubmit(event));
    }

    if (this.fallbackButton) {
      this.fallbackButton.addEventListener('click', () => this.handleDownloadClick());
    }
  }

  /**
   * Asks the baetter-downloads app (app proxy) for a short-lived link. The app
   * only answers for a lead with the download's tag (`sample:<handle>` for a
   * product sample, `<pdf>-download` for a blog PDF).
   * Logged-in customers are identified by Shopify, so the email is optional.
   * For an account PDF request, explicit consent lets the app add the PDF tags.
   */
  async handleDownloadClick() {
    if (this.downloadInFlight || !this.downloadEndpoint) return;

    this.downloadInFlight = true;
    this.setDownloadBusy(true);
    this.setDownloadError(false);

    try {
      const response = await fetch(this.downloadEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          [this.requestField]: this.requestValue,
          email: readCustomSampleDownloadEmail(),
          ...(this.customerAccountId ? { consent: this.downloadConsent } : {}),
        }),
      });
      const body = await response.json().catch(() => null);

      if (!response.ok || !body || typeof body.url !== 'string') {
        throw new Error(`Sample download request failed with ${response.status}`);
      }

      this.dispatchCustomEvent('custom:sample-download:completed', {
        productId: this.productId,
        productHandle: this.productHandle,
        via: 'manual-click',
      });

      if (this.customerAccountId) clearCustomPdfDownloadConsent();
      window.location.assign(body.url);
    } catch (error) {
      this.setDownloadError(true);
    } finally {
      this.downloadInFlight = false;
      this.setDownloadBusy(false);
    }
  }

  /**
   * @param {boolean} isBusy
   */
  setDownloadBusy(isBusy) {
    if (this.fallbackButton) {
      this.fallbackButton.disabled = isBusy;
      this.fallbackButton.setAttribute('aria-busy', isBusy ? 'true' : 'false');
    }
    if (this.fallbackDefaultLabel) {
      this.fallbackDefaultLabel.hidden = isBusy;
    }
    if (this.fallbackBusyLabel) {
      this.fallbackBusyLabel.hidden = !isBusy;
    }
  }

  /**
   * @param {boolean} isVisible
   */
  setDownloadError(isVisible) {
    if (this.downloadErrorElement) {
      this.downloadErrorElement.hidden = !isVisible;
    }
  }

  openDialog() {
    const dialog = this.dialogElement;
    if (!dialog) return;

    const alreadyOpen = dialog.open;

    try {
      if (typeof dialog.showModal === 'function' && !alreadyOpen) {
        dialog.showModal();
      } else if (!alreadyOpen) {
        dialog.setAttribute('open', '');
      }
    } catch (error) {
      dialog.setAttribute('open', '');
    }

    if (dialog.open && !alreadyOpen) {
      acquireCustomSampleDownloadScrollLock();
    }

    this.dispatchCustomEvent('custom:sample-download:opened', {
      productId: this.productId,
      productHandle: this.productHandle,
    });
  }

  closeDialog() {
    const dialog = this.dialogElement;
    if (!dialog || !dialog.open) return;

    try {
      if (typeof dialog.close === 'function') {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
    } catch (error) {
      try {
        dialog.removeAttribute('open');
      } catch (nestedError) {
        /* ignore */
      }
    }

    if (!dialog.open) {
      releaseCustomSampleDownloadScrollLock();
    }
  }

  showSuccessState() {
    if (this.formStateElement) {
      this.formStateElement.hidden = true;
    }
    if (this.successStateElement) {
      this.successStateElement.hidden = false;
    }
    if (this.formTitleElement) {
      this.formTitleElement.hidden = true;
    }
    if (this.successTitleElement) {
      this.successTitleElement.hidden = false;
    }
  }

  /**
   * @param {SubmitEvent} event
   */
  handleSubmit(event) {
    if (!this.formElement) return;

    const consentInput = /** @type {HTMLInputElement | null} */ (
      this.formElement.querySelector('input[name="custom_sample_consent"]')
    );
    if (consentInput && !consentInput.checked) {
      return;
    }

    const emailInput = /** @type {HTMLInputElement | null} */ (
      this.formElement.querySelector('input[name="contact[email]"]')
    );
    const firstNameInput = /** @type {HTMLInputElement | null} */ (
      this.formElement.querySelector('input[name="contact[first_name]"]')
    );
    const lastNameInput = /** @type {HTMLInputElement | null} */ (
      this.formElement.querySelector('input[name="contact[last_name]"]')
    );

    if (emailInput && emailInput.value.trim()) {
      rememberCustomSampleDownloadEmail(emailInput.value.trim());
    }
    if (this.customerAccountId && consentInput?.checked) {
      rememberCustomPdfDownloadConsent(this.formId, this.customerAccountId);
    }

    this.setSubmitBusy(true);

    this.dispatchCustomEvent('custom:sample-download:requested', {
      productId: this.productId,
      productHandle: this.productHandle,
      email: emailInput ? emailInput.value.trim() : '',
      firstName: firstNameInput ? firstNameInput.value.trim() : '',
      lastName: lastNameInput ? lastNameInput.value.trim() : '',
    });
  }

  /**
   * @param {boolean} isBusy
   */
  setSubmitBusy(isBusy) {
    if (this.submitButton) {
      this.submitButton.disabled = isBusy;
      this.submitButton.setAttribute('aria-busy', isBusy ? 'true' : 'false');
    }

    if (this.submitDefaultLabel) {
      this.submitDefaultLabel.hidden = isBusy;
    }

    if (this.submitBusyLabel) {
      this.submitBusyLabel.hidden = !isBusy;
    }
  }

  /**
   * @param {string} name
   * @param {Record<string, unknown>} detail
   */
  dispatchCustomEvent(name, detail) {
    this.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        composed: true,
        detail,
      })
    );
  }
}

if (!customElements.get('custom-sample-download')) {
  customElements.define('custom-sample-download', CustomSampleDownload);
}
